import { useMemo } from 'react';
import { useGqlQuery } from '@API/useAvQuery';
import { QueryKey } from '@fabric-ui/utils';
import { gql } from 'graphql-request';

const GET_MITRE_LIST = gql`
  query mitreAttacks {
    mitreAttacks {
      id
      tacticName
      techniqueName
    }
  }
`;
const convertArrToMitreObj = (obj, { id, tacticName, techniqueName }) => ({
  ...obj,
  [id]: `${tacticName} - ${techniqueName}`,
});

export default function useMitreObj(enabled = true) {
  const { data: origData, isLoading } = useGqlQuery<{ id: string; tacticName: string; techniqueName: string }[]>({
    queryKey: [QueryKey.MitreList],
    query: GET_MITRE_LIST,
    enabled,
  });
  const data = useMemo(
    () =>
      origData
        ? {
            mitreMap: origData.reduce(convertArrToMitreObj, {}),
            mitreOptions: origData.sort((a, b) => -b.id.localeCompare(a.id)),
            mitreSeparatedOptions: origData.map(({ id, tacticName, techniqueName }) => ({
              tacticId: id.split('|')[0],
              tacticName,
              techniqueId: id.split('|')[1],
              techniqueName,
            })),
          }
        : { mitreMap: {}, mitreOptions: [], mitreSeparatedOptions: [] },
    [origData]
  );

  const getMitreOnlyTacticLabel = val => {
    const idToFind = Array.isArray(val) ? val[0] : val;
    return data.mitreSeparatedOptions.find(({ tacticId }) => tacticId === idToFind)?.tacticName || idToFind;
  };

  const getMitreOnlyTacticId = val =>
    data.mitreSeparatedOptions.find(({ tacticName }) => tacticName.toLowerCase() === val.toLowerCase())?.tacticId || val;

  const getMitreOnlyTecnhiqueLabel = val => {
    const idToFind = Array.isArray(val) ? val[0] : val;
    return data.mitreSeparatedOptions.find(({ techniqueId }) => techniqueId === idToFind)?.techniqueName || idToFind;
  };

  const getMitreTacticLabel = row =>
    data.mitreSeparatedOptions.find(
      ({ tacticId, techniqueId }) => tacticId === row['alert.tactic'] && techniqueId === row['alert.technique']?.[0]
    )?.tacticName || row['alert.tactic'];
  const getMitreTechniqueLabel = row =>
    data.mitreSeparatedOptions.find(
      ({ tacticId, techniqueId }) => tacticId === row['alert.tactic'] && techniqueId === row['alert.technique']?.[0]
    )?.techniqueName || row['alert.technique'];
  const isMitreIdValid = (tacticId: string, techniqueId: string) => !!data.mitreMap[`${tacticId}|${techniqueId}`];

  return {
    ...data,
    isLoading,
    getMitreOnlyTacticLabel,
    getMitreOnlyTacticId,
    getMitreOnlyTecnhiqueLabel,
    getMitreTacticLabel,
    getMitreTechniqueLabel,
    isMitreIdValid,
  };
}
